import React from 'react';
import { TrendingUp, TrendingDown, Info, ChevronRight } from 'lucide-react';
import EmptyState from '../common/EmptyState';
import { TableRowSkeleton } from '../common/TableSkeleton';

/**
 * Call Center × Product performance table (Phase 5A + Phase 9 + Phase 12).
 *
 * `groups` is `performance_grouped` returned by GET /api/dashboard/kpis.
 * Each entry is a Call Center row with aggregated totals across all its
 * products, plus a `products` array with the per-product breakdown.
 *
 * UI (Phase 12):
 *   - One MAIN row per Call Center (collapsed by default) with global totals.
 *   - Chevron ▶ / ▼ toggles the expanded state.
 *   - When expanded, product rows appear underneath with a light indentation.
 *   - Product names longer than 35 characters are truncated with a native
 *     `title` tooltip that shows the full name on hover.
 *   - CC rows: bg #F0F4F8 · Product rows: bg #FFFFFF (indented).
 *
 * Insight badges (Phase 9) — attached to CC lines only:
 *   - {kind: 'return_rise',       variation_pct: +X} → "↑ Retour +X%" (orange)
 *   - {kind: 'confirmation_drop', variation_pct: -X} → "↓ Confirmation -X%" (red)
 *   - Threshold: CC must have ≥ 10 current orders (backend enforced).
 *   - Rates on CC lines are computed on CC totals (not averaged).
 */
const PRODUCT_MAX_CHARS = 35;

function fmtNumber(n) {
    if (n === null || n === undefined) return '—';
    return Number(n).toLocaleString('fr-FR');
}
function fmtPercent(n) {
    if (n === null || n === undefined) return '0%';
    return `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}%`;
}
function truncate(str, max = PRODUCT_MAX_CHARS) {
    if (!str) return '—';
    const s = String(str);
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// Single insight badge (pill + arrow + variation).
function InsightBadge({ badge, testId }) {
    if (badge.kind === 'return_rise') {
        const sign = badge.variation_pct > 0 ? '+' : '';
        return (
            <span
                data-testid={testId || `insight-badge-return_rise`}
                title="Taux de retour en hausse par rapport à la période précédente"
                className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-[#F59E0B]/15 text-[#F59E0B] text-[11px] font-semibold whitespace-nowrap"
            >
                <TrendingUp size={11} strokeWidth={2.25} />
                Retour {sign}{Number(badge.variation_pct).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}%
            </span>
        );
    }
    if (badge.kind === 'confirmation_drop') {
        const sign = badge.variation_pct > 0 ? '+' : '';
        return (
            <span
                data-testid={testId || `insight-badge-confirmation_drop`}
                title="Taux de confirmation en baisse par rapport à la période précédente"
                className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-[#EF4444]/15 text-[#EF4444] text-[11px] font-semibold whitespace-nowrap"
            >
                <TrendingDown size={11} strokeWidth={2.25} />
                Confirmation {sign}{Number(badge.variation_pct).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}%
            </span>
        );
    }
    return null;
}

export default function PerformanceTable({ groups, loading, error }) {
    const [openIds, setOpenIds] = React.useState(() => new Set());

    const toggle = React.useCallback((ccId) => {
        setOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(ccId)) next.delete(ccId);
            else next.add(ccId);
            return next;
        });
    }, []);

    const safeGroups = Array.isArray(groups) ? groups : [];

    return (
        <div data-testid="performance-table-card" className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)] overflow-hidden">
            <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
                <h3 className="text-base font-semibold text-[#1A1D2E]">Performance des Call Centers</h3>
                <span className="text-xs text-[#6B7280]">Cliquez sur ▶ pour afficher le détail par produit</span>
            </div>
            <div className="overflow-x-auto">
                <table data-testid="performance-table" className="w-full min-w-[720px] text-sm">
                    <thead>
                        <tr className="bg-[#F5F6FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                            <Th className="w-8"></Th>
                            <Th>Call Center</Th>
                            <Th className="text-right">Total Commandes</Th>
                            <Th className="text-right">Taux Conf.</Th>
                            <Th className="text-right">Taux Livr.</Th>
                            <Th className="text-right">Taux Retour</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <TableRowSkeleton rows={4} columns={6} testId="performance-skeleton" />
                        )}
                        {!loading && error && (
                            <tr>
                                <td data-testid="performance-error" colSpan={6} className="text-center py-10 text-[#EF4444] text-sm">{typeof error === 'string' ? error : 'Erreur'}</td>
                            </tr>
                        )}
                        {!loading && !error && safeGroups.length === 0 && (
                            <tr>
                                <td colSpan={6} className="py-2">
                                    <EmptyState
                                        testId="performance-empty"
                                        Icon={Info}
                                        title="Aucune donnée sur cette période"
                                        description="Ajustez la période ou les filtres pour voir la performance par call center."
                                    />
                                </td>
                            </tr>
                        )}
                        {!loading && !error && safeGroups.map((g) => {
                            const isOpen = openIds.has(g.call_center_id);
                            const badges = Array.isArray(g.insight_badges) ? g.insight_badges : [];
                            return (
                                <React.Fragment key={g.call_center_id}>
                                    {/* CC main row — 5 data columns (+ chevron) */}
                                    <tr
                                        data-testid="performance-cc-row"
                                        data-call-center-id={g.call_center_id}
                                        data-open={isOpen ? 'true' : 'false'}
                                        onClick={() => toggle(g.call_center_id)}
                                        className="bg-[#F0F4F8] hover:bg-[#E8EDF3] cursor-pointer border-t border-[#E5E7EB] transition-colors"
                                    >
                                        <Td className="w-8 text-center align-middle">
                                            <button
                                                type="button"
                                                data-testid={`performance-cc-toggle-${g.call_center_id}`}
                                                aria-expanded={isOpen}
                                                aria-label={isOpen ? 'Replier' : 'Déplier'}
                                                onClick={(e) => { e.stopPropagation(); toggle(g.call_center_id); }}
                                                className="inline-flex items-center justify-center w-6 h-6 rounded-md text-[#6B7280] hover:bg-white hover:text-[#1A1D2E]"
                                            >
                                                <ChevronRight
                                                    size={16}
                                                    strokeWidth={2.25}
                                                    className="transition-transform duration-150"
                                                    style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                                />
                                            </button>
                                        </Td>
                                        <Td>
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-semibold text-[#1A1D2E]">{g.call_center_label}</span>
                                                    {badges.map((b, i) => (
                                                        <InsightBadge
                                                            key={`${b.kind}-${i}`}
                                                            badge={b}
                                                            testId={`performance-badge-${g.call_center_id}-${b.kind}`}
                                                        />
                                                    ))}
                                                </div>
                                                <span className="text-[11px] text-[#6B7280]">{g.call_center_name}</span>
                                            </div>
                                        </Td>
                                        <Td className="text-right font-semibold">{fmtNumber(g.total)}</Td>
                                        <Td className="text-right font-semibold">{fmtPercent(g.confirmation_rate)}</Td>
                                        <Td className="text-right font-semibold">{fmtPercent(g.delivery_rate)}</Td>
                                        <Td className="text-right font-semibold">{fmtPercent(g.return_rate)}</Td>
                                    </tr>

                                    {/* Product breakdown — nested sub-table, 8 columns, only when expanded */}
                                    {isOpen && (
                                        <tr data-testid="performance-products-container" data-call-center-id={g.call_center_id} className="bg-white border-t border-[#F0F0F0]">
                                            <td className="w-8"></td>
                                            <td colSpan={5} className="p-0 pl-6 pr-3 pb-2">
                                                <table className="w-full text-sm border-t border-[#F0F0F0]">
                                                    <thead>
                                                        <tr className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                                                            <Th>Produit</Th>
                                                            <Th className="text-right">Total</Th>
                                                            <Th className="text-right">Confirmé</Th>
                                                            <Th className="text-right">In Delivery</Th>
                                                            <Th className="text-right">Sans Réponse</Th>
                                                            <Th className="text-right">Retourné</Th>
                                                            <Th className="text-right">Taux Conf.</Th>
                                                            <Th className="text-right">Taux Livr.</Th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(g.products || []).map((p) => (
                                                            <tr
                                                                key={`${g.call_center_id}-${p.product}`}
                                                                data-testid="performance-product-row"
                                                                data-call-center-id={g.call_center_id}
                                                                data-product={p.product}
                                                                className="border-t border-[#F0F0F0]"
                                                            >
                                                                <Td>
                                                                    <span className="inline-block text-[#1A1D2E]" title={p.product}>
                                                                        {truncate(p.product)}
                                                                    </span>
                                                                </Td>
                                                                <Td className="text-right">{fmtNumber(p.total)}</Td>
                                                                <Td className="text-right text-[#10B981]">{fmtNumber(p.confirmed)}</Td>
                                                                <Td className="text-right text-[#0D9488]">{fmtNumber(p.in_delivery)}</Td>
                                                                <Td className="text-right text-[#EF4444]">{fmtNumber(p.no_reply)}</Td>
                                                                <Td className="text-right text-[#DC2626]">{fmtNumber(p.returned)}</Td>
                                                                <Td className="text-right">{fmtPercent(p.confirmation_rate)}</Td>
                                                                <Td className="text-right">{fmtPercent(p.delivery_rate)}</Td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Th({ children, className = '' }) {
    return <th className={`text-left px-3 py-2 font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = '' }) {
    return <td className={`px-3 py-3 text-[#1A1D2E] align-middle ${className}`}>{children}</td>;
}
